import { MapPin } from '@phosphor-icons/react/dist/ssr/MapPin';
import { Plus } from '@phosphor-icons/react/dist/ssr/Plus';
import { IconCircleButton } from '../Icons/IconCircleButton';
import { UserCircle } from '@phosphor-icons/react/dist/ssr/UserCircle';
import ConnectionAvatar from '../ConnectionAvatar';

/**
 * ProfileConnectionsHeader Component
 *
 * Fixed header for ProfileConnectionsList — title row only.
 * Search bar and filter chips live in the scrollable content area.
 *
 * @param {node} devTools - Dev-only pill row rendered beside the title (variant
 *   switcher, LOC). A NODE, not a flag: the header stays presentational and
 *   never learns what a variant is or who is allowed to see one — the page owns
 *   the gate, this owns the placement.
 * @param {function} onManualAddClick - Handler for "+" button click
 * @param {boolean} isLocationSharing - Whether location sharing is enabled
 * @param {function} onLocationToggle - Handler for location sharing toggle
 */
const ProfileConnectionsHeader = ({
  devTools = null,
  onManualAddClick,
  isLocationSharing = false,
  onLocationToggle,
  // Simple-tier chrome (tiered-exchange Step 6, mock 7a): the header's right
  // side becomes the SP's own avatar — the profile-access point (no tab bar
  // at Simple). When spAvatar is set, the toggle + "+" are hidden.
  spAvatar = null, // { photoUrl, name }
  onSpAvatarClick,
}) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: '#FFFFFF',
        zIndex: 100,
      }}
    >
      {/* Safe area spacer for iOS */}
      <div
        style={{
          height: 'env(safe-area-inset-top)',
          backgroundColor: '#FFFFFF',
        }}
      />

      <div
        style={{
          width: '100%',
          maxWidth: '600px',
          margin: '0 auto',
          boxSizing: 'border-box',
          paddingTop: '12px',
          paddingBottom: '12px',
        }}
      >
        {/* Title Row: "People" + Location toggle + "+" button */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingLeft: '16px',
            paddingRight: '16px',
          }}
        >
          {/* Title + dev pills as one left-hand group, so the pills track the
              title rather than drifting toward the right-hand controls when the
              header's own contents change (SP avatar vs toggle + "+"). */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <span
              style={{
                fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
                fontSize: '32px',
                fontWeight: '800',
                color: '#000000',
              }}
            >
              People
            </span>
            {devTools}
          </div>

          {/* Right side: SP avatar (Simple tier) OR location toggle + "+" */}
          {spAvatar ? (
            <div onClick={onSpAvatarClick} style={{ cursor: 'pointer' }}>
              {/* No ring: this branch renders only for Simple profiles, and
                  the Royale ring marks FULL accounts only (user ruling
                  2026-08-08) */}
              {/* Outline UserCircle, not the name's first letter (user,
                  2026-08-20). `name` is deliberately NOT passed: ConnectionAvatar
                  renders photo → initial → fallbackIcon, so passing it would win
                  over the icon. The detail pages still get the initial — this is
                  scoped to the header, not a change to the shared component.
                  placeholderBg is transparent so the glyph's own circle is the
                  only circle. */}
              <ConnectionAvatar
                photoUrl={spAvatar.photoUrl}
                size={40}
                isRoyaleUser={false}
                placeholderBg="transparent"
                fallbackIcon={<UserCircle size={40} weight="regular" color="#8E8E93" />}
              />
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              {/* Location Sharing Toggle */}
              <IconCircleButton
                icon={MapPin}
                size={20}
                buttonSize={40}
                iconWeight={isLocationSharing ? 'light' : 'regular'}
                iconColor={isLocationSharing ? '#2E7D32' : '#8E8E93'}
                backgroundColor={
                  isLocationSharing ? 'rgba(76, 175, 80, 0.12)' : 'rgba(255, 255, 255, 0.95)'
                }
                onClick={onLocationToggle}
              />

              {/* Manual Add "+" Button */}
              <IconCircleButton
                icon={Plus}
                size={22}
                buttonSize={40}
                iconColor="#555555"
                onClick={onManualAddClick}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileConnectionsHeader;
